import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, ViewChild, inject, ChangeDetectionStrategy, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PartyFormComponent } from '../../../management-parties/components/party-form/party-form.component';
import { StepApprovalModalComponent, StepApprovalPayload } from '../step-approval-modal/step-approval-modal.component';
import { FormMode } from 'app/core/constants/form-mode';
import { CatalogService } from '@services/data/catalog.service';
import { PartyOverviewService } from '../../../management-parties/pages/party-overview/party-overview.service';
import { BpmService } from '../../services/bpm.service';
import { Request } from '../../services/requests.service';
import { StorageService } from '@services/core/storage.service';
import { AppSettings } from '@services/settings/app-settings.service';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { SheetSelectComponent } from 'app/core/components/sheet-select/sheet-select.component';

const VOLUNTARY_SLUG = 'UNSUBSCRIBE_VOLUNTARY';

@Component({
  selector: 'app-request-unsubscribe',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgSelectModule,
    PartyFormComponent,
    StepApprovalModalComponent,
    MobileFooterComponent,
    SheetSelectComponent
  ],
  providers: [PartyOverviewService],
  templateUrl: './request-unsubscribe.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./request-unsubscribe.component.scss']
})
export class RequestUnsubscribeComponent implements OnInit, OnChanges {
  @Input() partyData: any;
  @Input() partyForm!: FormGroup;
  @Input() vehicles: any[] = [];
  @Input() selectedVehicleUuid: string | null = null;
  @Input() currentEntityCode: any;
  @Input() mode: FormMode = FormMode.CREATE;
  @Input() requestData: any = null;
  /** Proceso inicial al abrir desde ficha (default voluntaria). */
  @Input() processSlug: string = VOLUNTARY_SLUG;
  /** Alcance de causales: 'party' muestra solo baja socio; 'admin' muestra todas. */
  @Input() unsubscribeScope: 'party' | 'admin' | 'all' = 'party';

  @Output() onClose = new EventEmitter<void>();
  @Output() onSubmitted = new EventEmitter<void>();

  @ViewChild('previewModal') previewModal: any;
  @ViewChild(StepApprovalModalComponent) approvalModal!: StepApprovalModalComponent;

  readonly appSettings = inject(AppSettings);
  private storageService = inject(StorageService);
  private bpmService = inject(BpmService);
  private catalogService = inject(CatalogService);

  private userRole(): string {
    return String(this.storageService.getJsonValue('role')?.name ?? '').toUpperCase();
  }

  /** Causales: si el scope es party solo muestra categoría socio; si es admin muestra todas. */
  causalesList = computed(() => {
    const cats = this.unsubscribeScope === 'party'
      ? ['UNSUBSCRIBE_PARTY']
      : this.bpmService.unsubscribeCategoriesForRole(this.userRole());
    return this._filterProcessList(new Set(cats));
  });

  private _filterProcessList(allowed: Set<string>): any[] {
    const list = (this.catalogService.processes() || [])
      .filter((p: any) => allowed.has(String(p.category || '').toUpperCase()))
      .map((p: any, idx: number) => ({
        id: idx + 1, code: p.slug, slug: p.slug,
        name: p.name, shortName: p.name, abbr: p.abbr
      }));
    return list.length ? list : [{ id: 1, code: VOLUNTARY_SLUG, slug: VOLUNTARY_SLUG, name: 'Baja voluntaria del socio', shortName: 'Baja voluntaria del socio', abbr: 'BAJ-V' }];
  }

  vehicleSelectItems(): any[] {
    return (this.vehicles || []).map((v: any) => ({
      ...v,
      selectLabel: `${v.plate} - ${v.brandName || v.brand?.name || ''} ${v.modelName || v.model?.name || ''} - ${v.year || ''}`.replace(/\s+/g, ' ').trim()
    }));
  }

  public unsubscribeForm: FormGroup;
  public formMode = FormMode;
  public pdfSrc: SafeResourceUrl | null = null;
  public isSubmitting = signal(false);
  public approvalRequest = signal<Request | null>(null);
  public token: string | null = null;
  public confirmedDate: string | null = null;

  private fb = inject(FormBuilder);
  private modalService = inject(NgbModal);
  private sanitizer = inject(DomSanitizer);
  private partyOverviewService = inject(PartyOverviewService);

  public Toast = Swal.mixin({
    toast: true, position: 'top-end',
    showConfirmButton: false, timer: 3000, timerProgressBar: true
  });

  constructor() {
    this.unsubscribeForm = this.fb.group({
      referenceUuid: [null, Validators.required],
      causalCode: [VOLUNTARY_SLUG, Validators.required],
      reason: [''],
      voto: ['']
    });

    this.unsubscribeForm.get('causalCode')?.valueChanges.subscribe((code) => {
      if (this.mode === FormMode.VIEW || !code) return;
      this._syncReasonFromCausal(code);
    });
  }

  ngOnInit(): void {
    this._initFormState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedVehicleUuid'] && this.selectedVehicleUuid) {
      this.unsubscribeForm.patchValue({ referenceUuid: this.selectedVehicleUuid });
    }
    if (changes['processSlug'] && this.processSlug && this.mode !== FormMode.VIEW) {
      this.unsubscribeForm.patchValue({ causalCode: this.processSlug });
    }
    if (changes['requestData'] && this.requestData) {
      this._initFormState();
    }
  }

  selectedProcessSlug(): string {
    return this.unsubscribeForm.get('causalCode')?.value || this.requestData?.processSlug || VOLUNTARY_SLUG;
  }

  isVoluntarySelected(): boolean {
    return this.bpmService.isVoluntaryUnsubscribe(this.selectedProcessSlug());
  }

  private _initFormState(): void {
    if (this.requestData) {
      const vUuid = this.requestData.vehicle?.uuid || this.requestData.referenceUuid;
      this.unsubscribeForm.patchValue({
        referenceUuid: vUuid,
        reason: this.requestData.reason,
        causalCode: this.requestData.processSlug || VOLUNTARY_SLUG
      });
      if (this.mode === FormMode.VIEW) {
        this.unsubscribeForm.disable();
      }
    } else {
      const initial = this.processSlug || VOLUNTARY_SLUG;
      this.unsubscribeForm.patchValue({ causalCode: initial });
      if (this.selectedVehicleUuid) {
        this.unsubscribeForm.patchValue({ referenceUuid: this.selectedVehicleUuid });
      } else if (this.vehicles && this.vehicles.length > 0) {
        this.unsubscribeForm.patchValue({ referenceUuid: this.vehicles[0].uuid });
      }
      this._syncReasonFromCausal(initial);
    }
  }

  getSelectedVehicle(): any {
    const uuid = this.unsubscribeForm.get('referenceUuid')?.value;
    return this.vehicles?.find(v => v.uuid === uuid) ?? this.requestData?.vehicle ?? null;
  }

  getCausalName(codeOrReason?: string): string {
    const code = codeOrReason || this.unsubscribeForm.get('causalCode')?.value || this.requestData?.processSlug || this.requestData?.reason;
    if (!code) return 'Baja voluntaria del socio';
    const found = this.causalesList().find(c => c.code === code || c.slug === code || c.name === code || c.shortName === code);
    if (found) return found.name;
    return this.bpmService.processOf(code)?.name || code;
  }

  findGroup(slug: string): string {
    const groups = this.catalogService.groups() ?? [];
    return groups.find(item => item.slug === slug)?.name ?? '';
  }

  getEntityName(code: number): string {
    const entities = this.catalogService.entities() ?? [];
    return entities.find(e => e.code === code)?.name ?? `${code}`;
  }

  getCurrentDateFormatted(): string {
    const now = new Date();
    return `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
  }

  onCausalChange(selected: any): void {
    if (selected) {
      const slug = selected.slug || selected.code;
      this.unsubscribeForm.patchValue({ causalCode: slug });
      this._syncReasonFromCausal(slug);
    }
  }

  private _syncReasonFromCausal(code: string): void {
    const selected = this.causalesList().find(c => c.code === code || c.slug === code);
    if (!selected) return;
    this.unsubscribeForm.patchValue({
      reason: `[${selected.abbr || selected.slug}] ${selected.shortName || selected.name}`
    }, { emitEvent: false });
  }

  /**
   * Envío alineado al BPM (mismo patrón que transferencia):
   * - Si el paso inicial exige documento → modal de voto/adjunto y luego POST.
   * - Si no → confirmación y POST directo.
   */
  async submitBaja(): Promise<void> {
    this._syncReasonFromCausal(this.selectedProcessSlug());
    if (!this.unsubscribeForm.get('referenceUuid')?.value || !this.unsubscribeForm.get('causalCode')?.value) {
      Swal.fire('Atención', 'Seleccione un vehículo y la causal de baja', 'warning');
      return;
    }
    if (this.isSubmitting()) return;

    const processSlug = this.selectedProcessSlug();

    try {
      const processes = await firstValueFrom(this.catalogService.loadProcesses());
      this.catalogService.processes.set(processes ?? []);
    } catch { /* usa catálogo en memoria */ }

    if (this.bpmService.initialStepRequiresDocument(processSlug)) {
      const statusSlug = this.bpmService.initialStatusSlug(processSlug);
      this.approvalRequest.set({
        processSlug,
        statusSlug,
        statusObj: { slug: statusSlug },
        entityCode: this.partyData?.entityCode ?? this.currentEntityCode ?? null
      } as unknown as Request);
      setTimeout(() => this.approvalModal?.open(), 0);
      return;
    }

    const confirmed = await Swal.fire({
      title: '¿Confirmar Solicitud?',
      text: 'Se enviará una solicitud de baja de cobertura.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, Enviar'
    });
    if (!confirmed.isConfirmed) return;

    await this._createRequest(null);
  }

  async onStepApprovalConfirmed(payload: StepApprovalPayload): Promise<void> {
    if (this.isSubmitting()) return;
    try {
      await this._createRequest(payload);
      this.approvalModal?.forceClose();
    } catch {
      /* error ya mostrado */
    }
  }

  private _buildPayload(bpmPayload: StepApprovalPayload | null): any {
    const slug = this.selectedProcessSlug();
    this._syncReasonFromCausal(slug);
    const causal = this.causalesList().find(c => c.code === slug || c.slug === slug);
    const reason = this.unsubscribeForm.get('reason')?.value
      || (causal ? `[${causal.abbr || slug}] ${causal.shortName || causal.name}` : slug);

    return {
      partyUuid: this.partyData?.uuid,
      processSlug: slug,
      entityCode: this.partyData?.entityCode ?? this.currentEntityCode,
      referenceUuid: this.unsubscribeForm.get('referenceUuid')?.value,
      reason,
      voto: bpmPayload?.gloss ?? null,
      gloss: bpmPayload?.gloss ?? null,
      documentUuid: bpmPayload?.documentUuid ?? null,
      documentTypeName: bpmPayload?.documentTypeName ?? null,
      requiresDocument: bpmPayload?.requiresDocument ?? false
    };
  }

  private async _createRequest(bpmPayload: StepApprovalPayload | null): Promise<void> {
    this.isSubmitting.set(true);
    try {
      const res: any = await firstValueFrom(
        this.partyOverviewService.createTransferRequest(this._buildPayload(bpmPayload))
      );
      this._handleRequestSuccess(res);
      this.approvalRequest.set(null);
      this.onSubmitted.emit();
    } catch (e: any) {
      await Swal.fire('Error', e?.error?.message ?? e?.message ?? 'No se pudo enviar la solicitud', 'error');
      throw e;
    } finally {
      this.isSubmitting.set(false);
    }
  }

  // Notifica registro exitoso mediante Toast y loguea enlace de prueba
  private _handleRequestSuccess(res: any): void {
    const token = (res?.data ?? res)?.confirmationToken;
    if (token) {
      console.info('[FPV] Enlace confirmacion socio:', `${window.location.origin}/confirm-request?token=${token}`);
    }
    const title = this.isVoluntarySelected()
      ? 'Solicitud registrada. Correo enviado al socio'
      : 'Solicitud de baja registrada con éxito';
    this.Toast.fire({ icon: 'success', title });
  }

  exportBajaPDF(): void {
    const doc = new jsPDF('p', 'pt', 'letter');
    const pdfWidth = doc.internal.pageSize.getWidth() - 35;
    const entityName = this.getEntityName(this.partyData?.entityCode ?? this.currentEntityCode);

    autoTable(doc, {
      html: '#hidden-baja-table', startY: 105, theme: 'grid', useCss: true,
      margin: { top: 110, bottom: 80, left: 35, right: 35 }
    });

    const totalPages = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      this._addHeaderPDF(doc, entityName, 'SOLICITUD DE BAJA DE COBERTURA');
      this._addFooterPDF(doc, pdfWidth, i, totalPages);
    }

    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    this.pdfSrc = this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrl);
    this.modalService.open(this.previewModal, { size: 'lg', backdrop: 'static' });
  }

  private _addHeaderPDF(doc: jsPDF, entityName: string, title: string): number {
    let currentY = 30;
    autoTable(doc, {
      startY: currentY,
      head: [
        [{ content: 'Fondo de Protección Vehicular', styles: { fontStyle: 'normal', fontSize: 10 } }],
        [{ content: entityName, styles: { fontStyle: 'italic', fontSize: 10 } }]
      ],
      theme: 'plain', headStyles: { halign: 'center', cellPadding: 1, textColor: [0, 0, 0] },
      margin: { left: 0, right: 0 }
    });

    autoTable(doc, {
      startY: currentY + 45,
      head: [[{ content: title, styles: { fontStyle: 'bold', fontSize: 11 } }]],
      theme: 'plain', headStyles: { halign: 'center', cellPadding: 0.3, textColor: [0, 0, 0] },
      margin: { left: 0, right: 0 }
    });
    return currentY;
  }

  private _addFooterPDF(doc: jsPDF, pdfWidth: number, pageNumber: number, totalPages: number): void {
    autoTable(doc, {
      startY: 742,
      body: [['Impreso: ' + this.getCurrentDateFormatted(), 'Sistema: FPV', 'Página ' + pageNumber + ' de ' + totalPages]],
      theme: 'plain', bodyStyles: { fontSize: 7, cellPadding: 0.3, textColor: [0, 0, 0] },
      didDrawPage: () => {
        doc.setLineWidth(0.5);
        doc.line(35, 737, pdfWidth, 737);
      }
    });
  }

  cancel(): void {
    this.onClose.emit();
  }
}
